import React from 'react'
import { makeStyles, useTheme } from '@mui/styles'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import MasterDetailModal from '@/modules/base/components/modal/master-detail-modal/master-detail-modal'
import SvgIcon from '@/modules/base/components/svg-icon/svg-icon'
import { ButtonSquare } from '@/modules/base/components/buttons'
import lang from '@/lang'
import { UNSAFE_openModal as openModal } from '@/modules/_helpers/openModal'
import config, { FeatureType, FeatureTypes } from '@/modules/base/components/modal/upgrade-modal/feature-locked/feature-config'
import { SubscriptionTypeTypes } from '@/modules/base/util/constants'
import { useUpgradeModal } from '@/hooks/useUpgradeModal'

import ProductMainIcon from '@assets/images/logo/svg/logo_whiteblue.svg'
import ProductDocumentIcon from '@assets/images/product/document_blue_with_icon.svg'
import ProductStaffIcon from '@assets/images/product/staff_blue_with_icon.svg'
import PackageIcon from '@assets/images/icons/package_2.svg'
import { isMobileScreen } from '@/modules/_helpers/detectMobile'
import ModalFullscreen from '@/modules/base/components/modal/fullscreen/modal-fullscreen'
import { useNavigate } from 'react-router'

const useStyles = makeStyles((theme) => ({
  contentWrapperLeft: {
    display: 'flex',
    flexDirection: 'column',
    padding: '40px 60px',
    height: '100%',
    backgroundColor: theme.darkBlueAlt,
    gap: 10,
  },
  contentClass: {
    width: '50%',
  },
  logo: {
    height: 32,
    minHeight: 32,
    width: 'auto',
    alignSelf: 'flex-start',
    '& #buchhaltung, & #dokumente, & #personal': {
      fill: theme.colors.white,
    },
  },
  contentWrapperRight: {
    display: 'flex',
    flexDirection: 'column',
    padding: '40px 60px',
    height: '100%',
    justifyContent: 'space-between',
    backgroundColor: theme.sidebarColorModal,
    color: theme.colors.white,
    gap: 20,
  },
  leftTitle: {
    fontSize: theme.fontSizes.xhuge,
    fontWeight: theme.fontWeights.medium,
    color: theme.colors.white,
    paddingTop: 30,
    lineHeight: '54px',
  },
  leftDescription: {
    color: theme.colors.white,
    fontStyle: 'italic',
  },
  logoContentWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px 0px',
    height: '100%',
  },
  subscriptionTypeLabel: {
    backgroundColor: theme.primaryBright,
    alignSelf: 'flex-start',
    color: theme.colors.white,
    padding: '10px 20px',
    borderRadius: 50,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    '& svg': {
      fill: theme.white,
      height: 16,
      width: 16,
    },
  },
  features: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 80,
    gap: 30,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 30,
  },
  featureItemTitle: {
    fontSize: theme.fontSizes.smedium,
    fontWeight: theme.fontWeights.semiBold,
    color: theme.colors.white,
    margin: 0,
    marginBottom: 5,
  },
  featureItemDescription: {
    fontSize: theme.fontSizes.smedium,
    color: theme.colors.white,
    margin: 0,
  },
  featureMedia: {
    width: 'fit-content',
    height: 'fit-content',
  },
  featureMediaVideo: {
    width: '100%',
    maxWidth: 560,
    aspectRatio: '16 / 9',
    border: 'none',
    borderRadius: 8,
  },
  footer: {
    display: 'flex',
    gap: 20,
    '& button': {
      width: 'fit-content',
      padding: '0px 30px',
    },
  },
  secondaryButton: {
    background: 'none',
    border: theme.border,
    borderColor: theme.primary,
    color: theme.white,
    '&:hover': {
      borderColor: theme.primaryV2,
      backgroundColor: theme.primaryV2,
      color: theme.white,
    },
  },
  featureItemIcon: {
    height: 30,
    width: 30,
    minWidth: 30,
    flexShrink: 0,
    fill: `${theme.primaryBright} !important`,
    '& path': {
      fill: `${theme.primaryBright} !important`,
    },
  },
  closeButton: {
    top: 30,
    right: 40,

    '& svg': {
      height: 16,
      width: 16,
    },
  },
  mobileModal: {
    '& .MuiDialogContent-root': {
      position: 'relative',

      backgroundColor: theme.sidebarColorModal,
    },
  },
  mobileContent: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.sidebarColorModal,
    height: '100%',

    '& $closeButton': {
      position: 'absolute',
      top: 20,
      right: 20,
    },

    '& $leftTitle': {
      fontSize: theme.fontSizes.xlarge,
      lineHeight: theme.fontSizes.xlarge,
      paddingTop: 30,
    },

    '& $features': {
      marginTop: 20,
    },

    '& $featureItem': {
      gap: 15,
    },

    '& $footer': {
      padding: 20,
      backgroundColor: theme.sidebarColorModal,
      justifyContent: 'center',
      flexShrink: 0,
    },
  },
  mobileScrollArea: {
    flex: 1,
    overflowY: 'auto',
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
}))

const subscriptionTypeLogos = {
  [SubscriptionTypeTypes.Main]: ProductMainIcon,
  [SubscriptionTypeTypes.Document]: ProductDocumentIcon,
  [SubscriptionTypeTypes.Staff]: ProductStaffIcon,
}

const skipPlanSelectionFeatures = [
  FeatureTypes.RECURRING_INVOICE,
  FeatureTypes.TIMETRACKING,
]

const FeatureLockedModal = (props) => {
  const { open, onClose, feature } = props
  const openUpgradeModal = useUpgradeModal()
  const theme = useTheme()

  const classes = useStyles()
  const navigate = useNavigate()
  const featureConfig = config[feature]

  const handleScheduleMeeting = () => {
    window.open('https://www.orgamax.de/orgamax-buchhaltung/orgamax-erweitern')
  }

  const handleUpgradeNow = () => {
    navigate('/shop')
    onClose()
  }

  const LeftContent = (
    <div className={classes.contentWrapperLeft}>
      <SvgIcon svg={subscriptionTypeLogos[featureConfig.subscriptionType]} className={classes.logo} />
      <div className={classes.leftTitle}>{featureConfig.title}</div>
      <div className={classes.leftDescription}>{featureConfig.description}</div>
      {featureConfig.featureMedia && <div className={classes.logoContentWrapper}>
        {featureConfig.mediaType === 'video'
          ? (
            <iframe
              src={featureConfig.featureMedia}
              title="YouTube video player"
              className={classes.featureMediaVideo}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
            )
          : (
            <img src={featureConfig.featureMedia} alt={featureConfig.title} className={classes.featureMedia} />
            )}
      </div>}
      {featureConfig.subscriptionTypeLabel && <div className={classes.subscriptionTypeLabel}>
        <SvgIcon svg={PackageIcon} />
        <span>{featureConfig.subscriptionTypeLabel}</span>
      </div>}
    </div>
  )

  const RightContent = (
    <div className={classes.contentWrapperRight}>
      <div className={classes.features}>
        {featureConfig.features.map((feature) => (
          <div className={classes.featureItem}>
            <feature.icon className={classes.featureItemIcon} />
            <div>
              <p className={classes.featureItemTitle}>{feature.title}</p>
              <p className={classes.featureItemDescription}>{feature.description}</p>
            </div>
          </div>
        ))}
      </div>
      <div className={classes.footer}>
        <ButtonSquare label={lang.generics.upgradeNow} onClick={handleUpgradeNow} />
        <ButtonSquare onClick={handleScheduleMeeting} variant='outline' label={lang.featureLockedModal.scheduleMeeting} className={classes.secondaryButton} />
      </div>
    </div>
  )

  if (!open) return null

  if (isMobileScreen()) {
    return (
      <ModalFullscreen
        open={open}
        onClose={onClose}
        className={classes.mobileModal}
        dataQsId='featureLockedModal'
      >
        <div className={classes.mobileContent}>
          <CloseRoundedIcon fontSize='medium' htmlColor={theme.white} className={classes.closeButton} onClick={onClose} />
          <div className={classes.mobileScrollArea}>
            <div className={classes.leftTitle}>{featureConfig.title}</div>
            <div className={classes.leftDescription}>{featureConfig.description}</div>
            <div className={classes.features}>
              {featureConfig.features.map((feature) => (
                <div className={classes.featureItem}>
                  <feature.icon className={classes.featureItemIcon} />
                  <div>
                    <p className={classes.featureItemTitle}>{feature.title}</p>
                    <p className={classes.featureItemDescription}>{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className={classes.footer}>
            <ButtonSquare label={lang.generics.upgradeNow} onClick={handleUpgradeNow} />
          </div>
        </div>
      </ModalFullscreen>
    )
  }

  return (
    <>
      <MasterDetailModal
        dataQsId='onboardingTesterStart-modal'
        showCloseIcon
        open={open}
        onClose={onClose}
        width="1200px"
        height="750px"
        leftPanelContent={LeftContent}
        rightPanelContent={RightContent}
        leftContentClass={classes.contentClass}
        rightContentClass={classes.contentClass}
        disableBackdropClick={false}
        closeButtonClass={classes.closeButton}
      >
      </MasterDetailModal>
    </>
  )
}

export const openFeatureLockedModal = (feature: FeatureType = FeatureTypes.ECONOMIC_ASSET) => {
  return openModal((close) => ({
    component: 'ModalSmall',
    children: <FeatureLockedModal open={true} onClose={close} feature={feature} />,
    props: {
      onClose: close,
    },
  }))
}

export default FeatureLockedModal
